import { forwardRef } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Button } from '@cannect/new-components/atoms'
import { ACOLHIMENTO_WHATSAPP_URL } from '@cannect/services/resources/recurrency/constants'
import { daysUntil, formatApiDate, zipReasons } from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDocumentType } from '@cannect/services/resources/recurrency/requests'
import type { DocumentStatus, SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'

const copy = subscriptionsCopy.documents

export const ANVISA_GUIDE_URL =
  'https://cannect-upload-homolog.s3.us-east-2.amazonaws.com/Documentos/Modelos/PASSO-A-PASSO-ANVISA.pdf'

const statusLine = (doc?: DocumentStatus | null) => {
  if (!doc || doc.status === 'none') return { text: copy.none }
  if (doc.status === 'pending_review') return { text: copy.pendingReview }
  if (doc.status === 'expired') {
    return { chip: { text: copy.expired(formatApiDate(doc.expiresAt)), tone: 'danger' as const } }
  }
  if (doc.status === 'valid') {
    return {
      text: doc.expiresAt ? copy.valid(formatApiDate(doc.expiresAt)) : 'Válida',
      chip:
        doc.expiringSoon && doc.expiresAt
          ? { text: copy.expiringSoon(daysUntil(doc.expiresAt)), tone: 'warning' as const }
          : undefined
    }
  }
  return { text: doc.status }
}

// Documento precisa de ação do paciente (renovar ou enviar).
export const documentNeedsAction = (
  detail: Pick<SubscriptionDetail, 'pauseReason' | 'documents'>,
  type: SubscriptionDocumentType
) => {
  const doc = detail.documents?.[type]
  if (doc?.status === 'pending_review') return false
  if (type === 'prescription' && detail.pauseReason === 'document') return true
  return !doc || doc.status === 'expired' || doc.status === 'none' || Boolean(doc.expiringSoon)
}

// Envio pelo portal: só titular (delta D4); receita de prescritor Cannect é renovada em consulta.
export const canUploadDocument = (detail: SubscriptionDetail, type: SubscriptionDocumentType) =>
  !detail.isDependent && (type === 'anvisa' || !detail.documents?.prescription?.prescriberIsCannect)

type DocumentsSectionProps = {
  detail: SubscriptionDetail
  reasons?: { reasons: string[]; messages: string[] } | null
  uploaded: SubscriptionDocumentType[]
  onUpload: (type: SubscriptionDocumentType) => void
}

export const DocumentsSection = forwardRef<HTMLElement, DocumentsSectionProps>(
  ({ detail, reasons, uploaded, onUpload }, ref) => {
    const open = detail.status === 'active' || detail.status === 'paused'
    const reasonList = reasons ? zipReasons(reasons) : []

    const renderActions = (type: SubscriptionDocumentType) => {
      if (!open) return null
      if (uploaded.includes(type)) {
        return (
          <p role="status" className="text-sm text-neutral-700">
            {type === 'prescription' ? copy.prescriptionSent : copy.anvisaSent}
          </p>
        )
      }
      if (!documentNeedsAction(detail, type)) return null

      if (detail.isDependent) {
        return (
          <p className="text-sm text-neutral-700">
            {copy.dependent(detail.personName || 'seu dependente')}.{' '}
            <a
              href={ACOLHIMENTO_WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary-700 underline">
              {subscriptionsCopy.delivery.talkToUs}
            </a>
          </p>
        )
      }

      if (type === 'prescription') {
        return canUploadDocument(detail, type) ? (
          <Button size="md" onClick={() => onUpload('prescription')}>
            {copy.sendPrescription}
          </Button>
        ) : (
          <Button asChild size="md">
            <Link to="/agendamento">{copy.schedule}</Link>
          </Button>
        )
      }

      return (
        <div className="flex flex-wrap items-center gap-3">
          <a
            href={ANVISA_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-primary-700 underline">
            {copy.anvisaHowTo}
          </a>
          <Button size="md" onClick={() => onUpload('anvisa')}>
            {copy.sendAnvisa}
          </Button>
        </div>
      )
    }

    const renderDocument = (type: SubscriptionDocumentType) => {
      const line = statusLine(detail.documents?.[type])
      return (
        <div key={type} className="flex flex-col gap-2 rounded-lg border border-solid border-neutral-100 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-neutral-800">
              {type === 'prescription' ? copy.prescription : copy.anvisa}
            </span>
            {line.chip && (
              <Badge
                size="xs"
                className={
                  line.chip.tone === 'danger'
                    ? 'bg-red-100 !text-xs text-red-800 hover:bg-red-100'
                    : 'bg-warning-200 !text-xs text-neutral-800 hover:bg-warning-200'
                }>
                {line.chip.text}
              </Badge>
            )}
          </div>
          {line.text && <span className="text-sm text-neutral-700">{line.text}</span>}
          {renderActions(type)}
        </div>
      )
    }

    return (
      <section
        id="documentos"
        ref={ref}
        tabIndex={-1}
        aria-labelledby="sub-documents"
        className="flex flex-col gap-4 rounded-xl border border-solid border-neutral-100 bg-white p-4 outline-none focus-visible:ring-2 focus-visible:ring-primary-700 md:p-6">
        <h2 id="sub-documents" className="text-lg font-semibold text-neutral-800">
          {subscriptionsCopy.sections.documents}
        </h2>

        {reasonList.length > 0 && (
          <div role="alert" className="flex flex-col gap-1 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
            <span className="font-semibold">{copy.reasonsTitle}</span>
            <ul className="m-0 list-disc pl-5">
              {reasonList.map((reason) => (
                <li key={reason.code}>{reason.message}</li>
              ))}
            </ul>
          </div>
        )}

        {detail.documents === null ? (
          <p className="text-sm text-neutral-700">{copy.loadError}</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {renderDocument('prescription')}
            {renderDocument('anvisa')}
          </div>
        )}
      </section>
    )
  }
)

DocumentsSection.displayName = 'DocumentsSection'
